import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { Truck, Users, List, Trash2, Star } from "react-feather";
import CollapsibleCard, { PANEL_ACCENT } from "../../sales/components/CollapsibleCard";
import InfoTile from "../../../components/common/InfoTile";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { MAROON } from "../../../components/common/brandColors";
import {
  useGetFuelCardQuery,
  useGetFuelCardLedgerQuery,
  useGetFuelCardUsersQuery,
  useGetFuelCardVehiclesQuery,
  useGetFuelVehiclesQuery,
  useLinkFuelCardUserMutation,
  useLinkFuelCardVehicleMutation,
  useUnlinkFuelCardUserMutation,
  useUnlinkFuelCardVehicleMutation,
} from "../fuelApi";
import { formatDate, formatDateTime, money } from "../fuelFormat";
import FuelUserSelect from "./FuelUserSelect";

// ---- Vehicles linked to this card ----
const VehiclesPanel = ({ card, canManage }) => {
  const { data: links = [], isLoading } = useGetFuelCardVehiclesQuery({ fuelCardId: card.fuelCardId });
  // Only the card's own branch - the server refuses a vehicle from another one.
  const { data: vehicles = [] } = useGetFuelVehiclesQuery({ branchId: card.branchId }, { skip: !canManage });
  const [linkVehicle, { isLoading: isLinking }] = useLinkFuelCardVehicleMutation();
  const [unlinkVehicle] = useUnlinkFuelCardVehicleMutation();
  const [vehicleId, setVehicleId] = useState("");
  const [asDefault, setAsDefault] = useState(false);

  const linkedIds = useMemo(() => new Set(links.map((l) => l.vehicleId)), [links]);
  const candidates = vehicles.filter((v) => !linkedIds.has(v.vehicleId));

  const handleAdd = async () => {
    if (!vehicleId) return;
    try {
      await linkVehicle({ fuelCardId: card.fuelCardId, vehicleId: Number(vehicleId), isDefault: asDefault }).unwrap();
      toast.success("Vehicle linked to the card");
      setVehicleId("");
      setAsDefault(false);
    } catch (error) {
      toast.error(error?.message || "Could not link the vehicle");
    }
  };

  // Same POST as linking - it is how the default is switched on an existing link.
  const handleToggleDefault = async (link) => {
    try {
      await linkVehicle({ fuelCardId: card.fuelCardId, vehicleId: link.vehicleId, isDefault: !link.isDefault }).unwrap();
      toast.success(link.isDefault ? "No longer the default card for that vehicle" : `Now the default card for ${link.regNo}`);
    } catch (error) {
      toast.error(error?.message || "Could not change the default");
    }
  };

  const handleRemove = async (link) => {
    const confirmed = await confirmDelete({
      title: `Remove ${link.regNo} from this card?`,
      text: "Purchases already recorded are not affected.",
    });
    if (!confirmed) return;
    try {
      await unlinkVehicle({ fuelCardId: card.fuelCardId, vehicleId: link.vehicleId }).unwrap();
      toast.success("Vehicle removed from the card");
    } catch (error) {
      toast.error(error?.message || "Could not remove the vehicle");
    }
  };

  return (
    <CollapsibleCard
      title="Vehicles"
      icon={Truck}
      accent={PANEL_ACCENT.link}
      hasData={links.length > 0}
      count={links.length}
      defaultOpen
    >
      <p className="text-muted small">
        A card can serve several vehicles, and a vehicle can have several cards. <b>Default</b> marks this card as the
        one that vehicle normally uses (offered first in the mobile app). Fuelling a vehicle with a card that is not linked
        to it at all is flagged as a mismatched card.
      </p>
      {isLoading && <p className="text-muted small mb-2">Loading…</p>}
      {!isLoading && links.length === 0 && <p className="text-muted small mb-2">No vehicles linked yet.</p>}
      {links.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Default card for it</th>
                <th>Linked</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.linkId}>
                  <td>
                    <span className="fw-semibold">{link.regNo}</span>
                    {!link.vehicleIsActive && <span className="badge badge-linedanger ms-2">Inactive</span>}
                    <div className="small text-muted">{link.brand} {link.model}</div>
                  </td>
                  <td>
                    {canManage ? (
                      <div className="form-check form-switch mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(link.isDefault)}
                          disabled={isLinking}
                          onChange={() => handleToggleDefault(link)}
                        />
                        <label className="form-check-label small">{link.isDefault ? "Default" : "Not default"}</label>
                      </div>
                    ) : link.isDefault ? (
                      <span className="badge badge-linesuccess">Default</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="small text-muted">
                    {formatDate(link.assignedDate)}
                    {link.assignedByName ? ` · ${link.assignedByName}` : ""}
                  </td>
                  {canManage && (
                    <td className="text-end">
                      <button type="button" className="btn btn-link p-1 text-danger" title="Remove" onClick={() => handleRemove(link)}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <select className="form-control form-control-sm" style={{ width: 280 }} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">Add a vehicle ({card.branchCode})…</option>
            {candidates.map((v) => (
              <option key={v.vehicleId} value={v.vehicleId}>
                {v.regNo} - {v.brand} {v.model}{v.defaultCardNumber ? ` (default: ${v.defaultCardNumber})` : ""}
              </option>
            ))}
          </select>
          <div className="form-check mb-0">
            <input type="checkbox" className="form-check-input" id="fuelLinkAsDefault" checked={asDefault} onChange={(e) => setAsDefault(e.target.checked)} />
            <label className="form-check-label small" htmlFor="fuelLinkAsDefault">Make this its default card</label>
          </div>
          <button type="button" className="btn btn-sm btn-primary" disabled={!vehicleId || isLinking} onClick={handleAdd}>
            {isLinking ? "Saving…" : "Link vehicle"}
          </button>
        </div>
      )}
    </CollapsibleCard>
  );
};

// ---- Users authorised for this card ----
const UsersPanel = ({ card, canManage }) => {
  const { data: links = [], isLoading } = useGetFuelCardUsersQuery({ fuelCardId: card.fuelCardId });
  const [linkUser, { isLoading: isLinking }] = useLinkFuelCardUserMutation();
  const [unlinkUser] = useUnlinkFuelCardUserMutation();
  const [picked, setPicked] = useState(null);

  // Identity is the userId - names are display text only.
  const linkedIds = useMemo(() => new Set(links.map((l) => l.userId)), [links]);

  const handleAdd = async () => {
    if (!picked) return;
    try {
      await linkUser({ fuelCardId: card.fuelCardId, userId: picked.value }).unwrap();
      toast.success("User authorised for the card");
      setPicked(null);
    } catch (error) {
      toast.error(error?.message || "Could not authorise the user");
    }
  };

  const handleRemove = async (link) => {
    const confirmed = await confirmDelete({
      title: `Remove ${link.userFullName || link.username} from this card?`,
      text: "They will no longer be able to use it, unless it is assigned to the vehicle they are fuelling.",
    });
    if (!confirmed) return;
    try {
      await unlinkUser({ fuelCardId: card.fuelCardId, userId: link.userId }).unwrap();
      toast.success("User removed from the card");
    } catch (error) {
      toast.error(error?.message || "Could not remove the user");
    }
  };

  return (
    <CollapsibleCard
      title="Authorised users"
      icon={Users}
      accent={PANEL_ACCENT.work}
      hasData={links.length > 0}
      count={links.length}
      defaultOpen
    >
      <p className="text-muted small">
        These people may use this card for <b>any</b> vehicle of the branch. Anyone else can only use it for the vehicles
        linked above.
      </p>
      {isLoading && <p className="text-muted small mb-2">Loading…</p>}
      {!isLoading && links.length === 0 && <p className="text-muted small mb-2">No users authorised yet.</p>}
      {links.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th>User</th>
                <th>Authorised</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.linkId}>
                  <td>
                    <span className="fw-semibold">{link.userFullName || link.username}</span>
                    {!link.userIsActive && <span className="badge badge-linedanger ms-2">Inactive</span>}
                    <div className="small text-muted">{link.username}</div>
                  </td>
                  <td className="small text-muted">
                    {formatDate(link.assignedDate)}
                    {link.assignedByName ? ` · ${link.assignedByName}` : ""}
                  </td>
                  {canManage && (
                    <td className="text-end">
                      <button type="button" className="btn btn-link p-1 text-danger" title="Remove" onClick={() => handleRemove(link)}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ width: 340 }}>
            <FuelUserSelect
              value={picked}
              onChange={setPicked}
              excludeUserIds={linkedIds}
              placeholder="Search a user by name or username…"
              inModal
            />
          </div>
          <button type="button" className="btn btn-sm btn-primary" disabled={!picked || isLinking} onClick={handleAdd}>
            {isLinking ? "Saving…" : "Authorise user"}
          </button>
        </div>
      )}
    </CollapsibleCard>
  );
};

// ---- Card statement ----
const StatementPanel = ({ card }) => {
  const { data: entries = [], isLoading } = useGetFuelCardLedgerQuery({ fuelCardId: card.fuelCardId });
  const totalIn = entries.reduce((sum, e) => sum + Number(e.amountIn || 0), 0);
  const totalOut = entries.reduce((sum, e) => sum + Number(e.amountOut || 0), 0);

  return (
    <CollapsibleCard
      title="Statement"
      icon={List}
      accent={PANEL_ACCENT.money}
      hasData={entries.length > 0}
      count={entries.length}
      summary={entries.length > 0 ? `Balance ${money(card.currentBalance)}` : undefined}
      defaultOpen={false}
    >
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3"><InfoTile label="Refilled (in)" tone="success" value={money(totalIn)} /></div>
        <div className="col-6 col-md-3"><InfoTile label="Purchases (out)" value={money(totalOut)} /></div>
        <div className="col-6 col-md-3"><InfoTile label="Current balance" tone="maroon" value={money(card.currentBalance)} /></div>
        <div className="col-6 col-md-3"><InfoTile label="Total refilled, all time" value={money(card.totalRefilled)} /></div>
      </div>
      {isLoading && <p className="text-muted small mb-0">Loading…</p>}
      {!isLoading && entries.length === 0 && <p className="text-muted small mb-0">Nothing on this card yet.</p>}
      {entries.length > 0 && (
        <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry</th>
                <th>Description</th>
                <th className="text-end">Refill in</th>
                <th className="text-end">Purchase out</th>
                <th className="text-end">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={`${e.entryType}-${e.recordGuid}`}>
                  <td className="text-nowrap">{formatDateTime(e.entryDate)}</td>
                  <td>
                    <span className={`badge ${e.entryType === "REFILL" ? "badge-linesuccess" : "badge-linesecondary"}`}>
                      {e.entryType === "REFILL" ? "Refill" : "Purchase"}
                    </span>
                    <div className="small text-muted">{e.documentNumber}</div>
                  </td>
                  <td>
                    {e.description || "-"}
                    {e.recordedByName && <div className="small text-muted">{e.recordedByName}</div>}
                  </td>
                  <td className="text-end" style={{ color: "#1E7E34" }}>{e.amountIn ? money(e.amountIn) : ""}</td>
                  <td className="text-end">{e.amountOut ? money(e.amountOut) : ""}</td>
                  <td className="text-end fw-semibold">{money(e.balanceAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
};

/**
 * Everything attached to one fuel card: which vehicles it serves, who may use
 * it, and its statement. Reading needs any office permission; changing links
 * needs Fuel.ManageCards.
 */
const FuelCardManageModal = ({ fuelCardId, canManage, onClose }) => {
  // Re-read by id so the balance and counts in the header follow every change made here.
  const { data: card } = useGetFuelCardQuery(fuelCardId, { skip: !fuelCardId });

  return (
    <Modal show={Boolean(fuelCardId)} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Star size={18} style={{ color: MAROON }} />
          {card ? (
            <span>
              {card.cardNumber}
              <span className="text-muted fw-normal ms-2" style={{ fontSize: 14 }}>
                {card.provider} · {card.branchCode}
              </span>
            </span>
          ) : (
            "Fuel card"
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!card && <p className="text-muted mb-0">Loading…</p>}
        {card && (
          <>
            <VehiclesPanel card={card} canManage={canManage} />
            <UsersPanel card={card} canManage={canManage} />
            <StatementPanel card={card} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelCardManageModal;
